import { MISSION_BY_ID, MISSION_IDS } from '../data/missions';
import {
  applyLoan,
  applyMissionFailure,
  applyMissionSuccess,
  applySwapCost,
  calculateFinalScore,
  deductBet,
  validateBet,
} from './chipUtils';
import { evaluateMissionById } from './missionEvaluator';
import type { GameAction, GameState, Player } from './types';
import {
  BOARD_SLOTS,
  DICE_PER_PLAYER,
  DRAFT_TURNS,
  HUMAN_PLAYER_INDEX,
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROUNDS,
  STARTING_CHIPS,
  leftOf,
  rightOf,
} from './types';

export interface GameActionError {
  action: GameAction;
  message: string;
}

function cloneState(state: GameState): GameState {
  return {
    ...state,
    players: state.players.map((p) => ({
      ...p,
      missionHand: [...p.missionHand],
      draftedMissions: [...p.draftedMissions],
      board: [...p.board],
      tray: [...p.tray],
    })),
    missionDraftHands: state.missionDraftHands.map((h) => [...h]),
    unusedMissionDeck: [...state.unusedMissionDeck],
    finalScores: [...state.finalScores],
    winners: [...state.winners],
    humanRoundHistory: [...state.humanRoundHistory],
  };
}

function nextRng(rngState: number): [number, number] {
  const next = (rngState * 1664525 + 1013904223) >>> 0;
  return [next, next / 0xffffffff];
}

function rollDie(rngState: number): [number, number] {
  const [next, rand] = nextRng(rngState);
  return [next, Math.floor(rand * 6) + 1];
}

function shuffle<T>(items: T[], rngState: number): [T[], number] {
  const arr = [...items];
  let state = rngState;
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const [next, rand] = nextRng(state);
    state = next;
    const j = Math.floor(rand * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return [arr, state];
}

function createEmptyBoard(): (number | null)[] {
  return Array.from({ length: BOARD_SLOTS }, () => null);
}

function createPlayer(index: number, isHuman: boolean): Player {
  return {
    index,
    name: isHuman ? '나' : `플레이어 ${index + 1}`,
    isHuman,
    chips: STARTING_CHIPS,
    loanTokens: 0,
    missionHand: [],
    selectedMissionId: null,
    betAmount: 0,
    board: createEmptyBoard(),
    tray: [],
    missionSwappedThisRound: false,
    draftedMissions: [],
  };
}

export function createInitialState(): GameState {
  return {
    phase: 'setup',
    playerCount: 0,
    players: [],
    round: 0,
    draftTurn: 0,
    activePlayerIndex: 0,
    dealerIndex: 0,
    missionDraftRound: 0,
    missionDraftHands: [],
    settlementIndex: 0,
    houseChips: 510,
    unusedMissionDeck: [],
    seed: 1,
    rngState: 1,
    finalScores: [],
    winners: [],
    message: '플레이어 수를 선택하세요 (3-6)',
    humanRoundHistory: [],
  };
}

function setupGame(state: GameState, playerCount: number, seed?: number): GameState {
  if (playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
    throw new Error(`플레이어 수는 ${MIN_PLAYERS}-${MAX_PLAYERS}명이어야 합니다`);
  }

  const next = cloneState(state);
  const initialSeed = seed ?? Date.now();
  next.seed = initialSeed;
  next.rngState = initialSeed >>> 0;
  next.playerCount = playerCount;
  next.players = Array.from({ length: playerCount }, (_, i) =>
    createPlayer(i, i === HUMAN_PLAYER_INDEX),
  );
  next.dealerIndex = 0;
  next.round = 0;
  next.phase = 'mission_draft';
  next.missionDraftRound = 0;
  next.activePlayerIndex = 0;
  next.humanRoundHistory = [];

  const [shuffled, rngAfterShuffle] = shuffle(MISSION_IDS, next.rngState);
  next.rngState = rngAfterShuffle;

  const dealCount = playerCount * 5;
  next.missionDraftHands = Array.from({ length: playerCount }, (_, i) =>
    shuffled.slice(i * 5, i * 5 + 5),
  );
  next.unusedMissionDeck = shuffled.slice(dealCount);
  next.message = '미션 카드 드래프트: 1장 선택 후 왼쪽으로 전달';

  return next;
}

function passMissionHandsLeft(state: GameState): void {
  const { playerCount, missionDraftHands } = state;
  const received: string[][] = Array.from({ length: playerCount }, () => []);
  for (let i = 0; i < playerCount; i += 1) {
    const target = leftOf(i, playerCount);
    received[target] = [...missionDraftHands[i]];
  }
  state.missionDraftHands = received;
}

function finalizeMissionDraft(state: GameState): void {
  for (const player of state.players) {
    player.missionHand = [...player.draftedMissions];
  }
  state.missionDraftHands = [];
  state.round = 1;
  state.phase = 'round_roll';
  state.activePlayerIndex = 0;
  state.message = `라운드 ${state.round}: 주사위를 굴리세요`;
}

function startRound(state: GameState): GameState {
  const next = cloneState(state);
  for (const player of next.players) {
    player.board = createEmptyBoard();
    player.selectedMissionId = null;
    player.betAmount = 0;
    player.missionSwappedThisRound = false;
    player.tray = [];
    for (let d = 0; d < DICE_PER_PLAYER; d += 1) {
      const [rng, die] = rollDie(next.rngState);
      next.rngState = rng;
      player.tray.push(die);
    }
  }
  next.draftTurn = 0;
  next.phase = 'mission_select';
  next.activePlayerIndex = 0;
  next.message = `라운드 ${next.round}: 미션 카드를 선택하세요`;
  return next;
}

function allPlayersSelectedMission(state: GameState): boolean {
  return state.players.every((p) => p.selectedMissionId !== null);
}

function allPlayersBet(state: GameState): boolean {
  return state.players.every((p) => p.betAmount > 0);
}

function startDraft(state: GameState): GameState {
  const next = cloneState(state);
  next.phase = 'draft';
  next.draftTurn = 1;
  next.activePlayerIndex = next.dealerIndex;
  next.message = `드래프트 ${next.draftTurn}턴: 주사위 1개를 오른쪽 플레이어에게 배치`;
  return next;
}

function passTraysLeft(state: GameState): void {
  const trays = state.players.map((p) => [...p.tray]);
  for (let i = 0; i < state.playerCount; i += 1) {
    state.players[leftOf(i, state.playerCount)].tray = trays[i];
  }
}

function draftTurnComplete(state: GameState): boolean {
  return state.players.every((p) => {
    if (state.draftTurn < DRAFT_TURNS) {
      return p.tray.length === DICE_PER_PLAYER - state.draftTurn;
    }
    return p.tray.length === 0;
  });
}

function advanceDraftTurn(state: GameState): GameState {
  const next = cloneState(state);

  if (next.draftTurn < 3) {
    passTraysLeft(next);
    next.draftTurn += 1;
    next.activePlayerIndex = next.dealerIndex;
    next.message = `드래프트 ${next.draftTurn}턴: 주사위 1개를 오른쪽 플레이어에게 배치`;
    return next;
  }

  if (next.draftTurn === 3) {
    passTraysLeft(next);
    next.draftTurn = 4;
    next.activePlayerIndex = next.dealerIndex;
    next.message = '드래프트 4턴: 1개는 오른쪽, 1개는 내 5번 칸';
    return next;
  }

  next.phase = 'settlement';
  next.settlementIndex = next.dealerIndex;
  next.message = '미션 공개 및 정산';
  return next;
}

function applySettlementReveal(state: GameState, playerIndex: number): GameState {
  const next = cloneState(state);
  const player = next.players[playerIndex];
  const missionId = player.selectedMissionId;
  if (!missionId) return next;

  const mission = MISSION_BY_ID[missionId];
  const boards = next.players.map((p) => p.board);
  const success = evaluateMissionById(playerIndex, next.playerCount, boards, missionId);

  if (player.isHuman) {
    next.humanRoundHistory.push({
      round: next.round,
      missionId,
      betAmount: player.betAmount,
      success,
      missionSwapped: player.missionSwappedThisRound,
    });
  }

  if (success) {
    const result = applyMissionSuccess(
      player.chips,
      player.betAmount,
      mission.payout,
      next.houseChips,
    );
    player.chips = result.playerChips;
    next.houseChips = result.houseChips;
    player.betAmount = 0;
  } else {
    const result = applyMissionFailure(player.chips, player.betAmount, next.houseChips);
    player.chips = result.playerChips;
    next.houseChips = result.houseChips;
    player.betAmount = 0;
  }

  player.missionHand = player.missionHand.filter((id) => id !== missionId);
  player.selectedMissionId = null;

  return next;
}

function applyLoansIfNeeded(state: GameState): GameState {
  const next = cloneState(state);
  if (next.round >= ROUNDS) return next;

  for (const player of next.players) {
    if (player.chips === 0) {
      const loan = applyLoan(player.chips, player.loanTokens, next.houseChips);
      if (loan) {
        player.chips = loan.playerChips;
        player.loanTokens = loan.loanTokens;
        next.houseChips = loan.houseChips;
      }
    }
  }
  return next;
}

function computeFinalScores(state: GameState): GameState {
  const next = cloneState(state);
  next.finalScores = next.players.map((player) => {
    const unusedPayout = player.missionHand.reduce(
      (sum, id) => sum + (MISSION_BY_ID[id]?.payout ?? 0),
      0,
    );
    return calculateFinalScore(player.chips, unusedPayout, player.loanTokens);
  });

  const maxScore = Math.max(...next.finalScores);
  next.winners = next.finalScores
    .map((score, index) => (score === maxScore ? index : -1))
    .filter((i) => i >= 0);
  next.phase = 'game_over';
  next.message = `게임 종료! 우승: ${next.winners.map((i) => next.players[i].name).join(', ')}`;
  return next;
}

function endRound(state: GameState): GameState {
  let next = applyLoansIfNeeded(state);

  if (next.round >= ROUNDS) {
    next = computeFinalScores(next);
    return next;
  }

  next = cloneState(next);
  next.round += 1;
  next.phase = 'round_roll';
  next.message = `라운드 ${next.round}: 주사위를 굴리세요`;
  return startRound(next);
}

export function getExpectedActor(state: GameState): number {
  return state.activePlayerIndex;
}

export function isHumanTurn(state: GameState): boolean {
  return state.players[state.activePlayerIndex]?.isHuman ?? false;
}

export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SETUP':
      return setupGame(state, action.playerCount, action.seed);

    case 'MISSION_DRAFT_PICK': {
      if (state.phase !== 'mission_draft') return state;
      if (action.playerIndex !== state.activePlayerIndex) return state;

      const next = cloneState(state);
      const hand = next.missionDraftHands[action.playerIndex];
      const pickIndex = hand.indexOf(action.missionId);
      if (pickIndex < 0) return state;

      hand.splice(pickIndex, 1);
      next.players[action.playerIndex].draftedMissions.push(action.missionId);

      const nextPlayer = (action.playerIndex + 1) % next.playerCount;
      next.activePlayerIndex = nextPlayer;

      if (nextPlayer === 0) {
        passMissionHandsLeft(next);
        next.missionDraftRound += 1;
        if (next.missionDraftRound >= 5) {
          finalizeMissionDraft(next);
          return startRound(next);
        }
        next.message = `미션 드래프트 ${next.missionDraftRound + 1}/5: 1장 선택`;
      }

      return next;
    }

    case 'ROLL_DICE':
      if (state.phase !== 'round_roll') return state;
      return startRound(state);

    case 'SELECT_MISSION': {
      if (state.phase !== 'mission_select') return state;
      if (action.playerIndex !== state.activePlayerIndex) return state;

      const player = state.players[action.playerIndex];
      if (!player.missionHand.includes(action.missionId)) return state;

      const next = cloneState(state);
      next.players[action.playerIndex].selectedMissionId = action.missionId;
      next.activePlayerIndex = (action.playerIndex + 1) % next.playerCount;

      if (allPlayersSelectedMission(next)) {
        next.phase = 'betting';
        next.activePlayerIndex = 0;
        next.message = '베팅: 미션 카드 위에 칩을 올리세요';
      }

      return next;
    }

    case 'PLACE_BET': {
      if (state.phase !== 'betting') return state;
      if (action.playerIndex !== state.activePlayerIndex) return state;

      const player = state.players[action.playerIndex];
      if (!validateBet(action.amount, player.chips)) return state;

      const next = cloneState(state);
      const p = next.players[action.playerIndex];
      p.chips = deductBet(p.chips, action.amount);
      p.betAmount = action.amount;
      next.activePlayerIndex = (action.playerIndex + 1) % next.playerCount;

      if (allPlayersBet(next)) {
        return startDraft(next);
      }

      return next;
    }

    case 'SWAP_MISSION': {
      if (state.phase !== 'draft') return state;
      if (action.playerIndex !== state.activePlayerIndex) return state;
      if (state.draftTurn < 1 || state.draftTurn > 3) return state;

      const next = cloneState(state);
      const p = next.players[action.playerIndex];
      if (p.missionSwappedThisRound) return state;
      if (!p.missionHand.includes(action.newMissionId)) return state;
      if (!p.selectedMissionId) return state;

      const swap = applySwapCost(p.betAmount, next.houseChips, state.draftTurn);
      if (!swap) return state;

      p.betAmount = swap.betAmount;
      next.houseChips = swap.houseChips;
      p.missionHand = p.missionHand.map((id) =>
        id === action.newMissionId ? p.selectedMissionId! : id,
      );
      p.selectedMissionId = action.newMissionId;
      p.missionSwappedThisRound = true;
      return next;
    }

    case 'DRAFT_PLACE': {
      if (state.phase !== 'draft') return state;
      if (state.draftTurn >= DRAFT_TURNS) return state;
      if (action.playerIndex !== state.activePlayerIndex) return state;

      const next = cloneState(state);
      const actor = next.players[action.playerIndex];
      if (action.trayIndex < 0 || action.trayIndex >= actor.tray.length) return state;

      const die = actor.tray.splice(action.trayIndex, 1)[0];
      const target = next.players[rightOf(action.playerIndex, next.playerCount)];
      const slot = state.draftTurn - 1;
      if (target.board[slot] !== null) return state;
      target.board[slot] = die;

      next.activePlayerIndex = rightOf(action.playerIndex, next.playerCount);

      if (next.activePlayerIndex === next.dealerIndex) {
        if (draftTurnComplete(next)) {
          return advanceDraftTurn(next);
        }
      }

      return next;
    }

    case 'DRAFT_TURN4': {
      if (state.phase !== 'draft' || state.draftTurn !== 4) return state;
      if (action.playerIndex !== state.activePlayerIndex) return state;

      const next = cloneState(state);
      const actor = next.players[action.playerIndex];
      if (actor.tray.length !== 2) return state;
      if (
        action.toRightTrayIndex === action.keepTrayIndex ||
        action.toRightTrayIndex < 0 ||
        action.keepTrayIndex < 0 ||
        action.toRightTrayIndex > 1 ||
        action.keepTrayIndex > 1
      ) {
        return state;
      }

      const toRightDie = actor.tray[action.toRightTrayIndex];
      const keepDie = actor.tray[action.keepTrayIndex];
      actor.tray = [];

      const target = next.players[rightOf(action.playerIndex, next.playerCount)];
      if (target.board[3] !== null || actor.board[4] !== null) return state;
      target.board[3] = toRightDie;
      actor.board[4] = keepDie;

      next.activePlayerIndex = rightOf(action.playerIndex, next.playerCount);

      if (next.activePlayerIndex === next.dealerIndex) {
        if (draftTurnComplete(next)) {
          return advanceDraftTurn(next);
        }
      }

      return next;
    }

    case 'SETTLEMENT_REVEAL': {
      if (state.phase !== 'settlement') return state;
      if (action.playerIndex !== state.settlementIndex) return state;

      const next = applySettlementReveal(state, action.playerIndex);

      if (rightOf(next.dealerIndex, next.playerCount) === action.playerIndex) {
        return endRound(next);
      }

      next.settlementIndex = leftOf(action.playerIndex, next.playerCount);
      return next;
    }

    case 'ADVANCE_PHASE':
      return state;

  case 'BOT_ACT':
      return state;

    default:
      return state;
  }
}

export function getBoards(state: GameState): (number | null)[][] {
  return state.players.map((p) => p.board);
}

export function getPlayerMissionIds(state: GameState, playerIndex: number): string[] {
  return state.players[playerIndex]?.missionHand ?? [];
}
